import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

import { isVisualizacaoModoDashboard, isVisualizacaoModoList, visualizacaoModoDashboard, visualizacaoModoList } from '../../../common/visualizacao-modo';
import { AuthService, BaseVisualizacaoService, ConfigService } from '../../../core';
import { ITabSettings } from '../../../domains/shared/components/novo-tabs/novo-tabs.component';
import { CampanhaFilterStorageService } from '../../campanhas/campanhas/filter/campanha-filter-storage.service';
import { CampanhaVisualizacaoStorageService } from '../../campanhas/campanhas/visualizacao/campanha-visualizacao-storage.service';
import { ConfigEmpresa, isConfigEmpresaVersaoIntegracaoErp1, isTipoMercadoUnidadeNegocio } from '../../configuracoes/app/empresa';
import { IntegrationClienteModalComponent } from '../../configuracoes/integration/cliente';
import { clienteCadastroCliente, clienteCadastroProspect } from './cadastro/cliente-cadastro';
import { Cliente } from './cliente';
import { CAMPANHAS_FILTER_STORAGE_KEY, CAMPANHAS_VISUALIZACAO_STORAGE_KEY } from './cliente-consts';
import { ClienteOptions } from './cliente-options';
import { ClienteDashboardComponent } from './dashboard';
import { ClienteContatoExcelExportModalComponent } from './excel';
import { ClienteFilter, ClienteFilterComponent, ClienteFilterStorageService } from './filter';
import { ClienteFilter as NovoClienteFilter } from './filter/novo-cliente-filter/cliente-filter';
import { ClienteImportModalComponent } from './import-modal';
import { ClienteIntegracaoErpImportModalComponent, ClienteIntegracaoErpImportService } from './integracoes/erp/import';
import { ClienteListComponent } from './list';
import { NovoClienteListComponent } from './list/novo-cliente-list/novo-cliente-list.component';
import { ClienteListagemReportModalComponent } from './listagem-report-modal';
import { ClienteUnidadeNegocioImportModalComponent } from './unidade-negocio';
import { ClienteVisualizacao, ClienteVisualizacaoStorageService } from './visualizacao';

@Component({
    selector: 'sgt-cliente',
    templateUrl: './cliente.component.html',
    styleUrls: ['./cliente.component.less']
})
export class ClienteComponent implements OnInit, AfterViewInit, OnDestroy {
    configEmpresa: ConfigEmpresa;
    isIntegracaoErpV1: boolean;
    isAdministrador: boolean;
    isAtendente: boolean;
    isRepresentante: boolean;
    isFinanceiro: boolean;

    entities: Cliente[];

    // Filter
    filter = new ClienteFilter();
    @ViewChild('clienteFilter') clienteFilter: ClienteFilterComponent;
    orderBy: number;
    orderByDir: string;

    // Visualização
    readonly visualizacaoStorageKey = 'clienteVisualizacao';
    visualizacao = new ClienteVisualizacao();
    visualizacaoModos = [visualizacaoModoList, visualizacaoModoDashboard];
    visualizacaoTabs: ITabSettings[] = [
      {
        value: String(visualizacaoModoList.id),
        label: visualizacaoModoList.nome,
        icon: visualizacaoModoList.iconClass
      },
      {
        value: String(visualizacaoModoDashboard.id),
        label: visualizacaoModoDashboard.nome,
        icon: visualizacaoModoDashboard.iconClass
      }
    ];

    @ViewChild('containerContent', {static: true}) containerContent: ElementRef;
    @ViewChild('clienteImportModal') clienteImportModal: ClienteImportModalComponent;
    @ViewChild('clienteUnidadeNegocioImportModal') clienteUnidadeNegocioImportModal: ClienteUnidadeNegocioImportModalComponent;
    @ViewChild('listagemReportModal') listagemReportModal: ClienteListagemReportModalComponent;
    @ViewChild('integrationModal') integrationModal: IntegrationClienteModalComponent;
    @ViewChild('integracaoErpImportModal') integracaoErpImportModal: ClienteIntegracaoErpImportModalComponent;


    @ViewChild('list') list: ClienteListComponent;
    @ViewChild('novoList') novoList: NovoClienteListComponent;
    @ViewChild('dashboard') dashboard: ClienteDashboardComponent;

    // Options
    options: ClienteOptions;

    // Excel
    @ViewChild('excelExportContatoModal') excelExportContatoModal: ClienteContatoExcelExportModalComponent;

    /** Subscription da execução da importação dos clientes do ERP */
    afterImportErpSub$: Subscription;

    constructor(private activatedRoute: ActivatedRoute,
                private authService: AuthService,
                private baseVisualizacaoService: BaseVisualizacaoService,
                private configService: ConfigService,
                private clienteFilterStorageService: ClienteFilterStorageService,
                private clienteVisualizacaoStorageService: ClienteVisualizacaoStorageService,
                private clienteIntegracaoErpImportService: ClienteIntegracaoErpImportService,
                private campanhaVisualizacaoStorageService: CampanhaVisualizacaoStorageService,
                private campanhaFilterStorageService: CampanhaFilterStorageService) { }


    ngOnInit() {
        this.configEmpresa = this.configService.getEmpresa();
        this.isIntegracaoErpV1 = isConfigEmpresaVersaoIntegracaoErp1(this.configEmpresa);
        this.isAdministrador = this.authService.isAdministrador();
        this.isAtendente = this.authService.isAtendente();
        this.isRepresentante = this.authService.isRepresentante();
        this.isFinanceiro = this.authService.isFinanceiro();
        this.createOptions();

        // Visualização
        this.visualizacao = this.clienteVisualizacaoStorageService.get(this.visualizacaoStorageKey);

        // Subscrevendo ao evento de finalização da importação dos clientes do ERP
        this.afterImportErp();

        // Removendo storages das campanhas
        this.removeCampanhasStorage();
    }

    /** Verifica se o Tipo de Mercado da empresa é por Unidade de Negócio */
    isTipoMercadoUnidadeNegocio(): boolean {
        if (!this.configEmpresa) {
            return false;
        }
        return isTipoMercadoUnidadeNegocio(this.configEmpresa.tipoMercado);
    }

    /** Cria as opções disponíveis para serem executadas pelos usuários */
    private createOptions() {
        this.options = new ClienteOptions();
        // Importar
        this.options.canImportar = this.isAdministrador;
        // Importar unidades de negócio
        this.options.canImportarUnidadeNegocio = this.isTipoMercadoUnidadeNegocio() && this.isAdministrador;
        // Imprimir listagem
        this.options.canImprimirListagem = true;
        // Importar do ERP V1
        this.options.canImportErp_v1 = this.canImportErp_v1();
        // Importar do ERP
        this.options.canImportErp = this.canImportErp();
    }

    private setStorage() {
        // Visualização
        this.clienteVisualizacaoStorageService.set(this.visualizacaoStorageKey, this.visualizacao);
        // Filter
        this.clienteFilterStorageService.set(this.filter as ClienteFilter);
    }

    get activeVisualizacaoTab(): string {
        return String(this.visualizacao?.modo);
    }

    onVisualizacaoTabChange(tabValue: string) {
        this.onChangeVisualizacaoModo(Number(tabValue));
    }

    /** Ao alterar o modo de visualização */
    onChangeVisualizacaoModo(modo: number) {
        this.visualizacao.modo = modo;
        setTimeout(() => this.search(), 0);
    }

    /** Verifica se o modo de visualização selecionado é o modo List */
    isVisualizacaoModoList(): boolean {
        return isVisualizacaoModoList(this.visualizacao?.modo);
    }

    /** Verifica se o modo de visualização selecionado é o modo Dashboard */
    isVisualizacaoModoDashboard(): boolean {
        return isVisualizacaoModoDashboard(this.visualizacao?.modo);
    }

    /** Seta os filtros padrões */
    private setDefaultFilters(force = false) {
        if (!this.clienteFilterStorageService.hasFilter() || force) {
            this.filter.cadastros = [clienteCadastroProspect.id, clienteCadastroCliente.id];
        }
    }

    /** Ao iniciar o filtro */
    onFilterInitialized(filter: ClienteFilter | NovoClienteFilter) {
        this.filter = filter as ClienteFilter;
        this.setDefaultFilters();
        this.setStorage();
        if (this.isVisualizacaoModoDashboard()) {
            this.dashboard?.load();
        }
    }

    /** Quando o filtro é alterado */
    onFilterChange(filter: ClienteFilter | NovoClienteFilter) {
        this.filter = filter as ClienteFilter;
        this.setStorage();
        if (this.isVisualizacaoModoDashboard()) {
            this.dashboard?.load();
        }
    }

    /** Ao limpar o filtro */
    onFilterCleaned() {
        this.setDefaultFilters(true);
        this.setStorage();
        if (this.isVisualizacaoModoDashboard()) {
            this.dashboard?.load();
        }
    }

    search() {
        this.setStorage();

        if (this.isVisualizacaoModoDashboard()) { // Dashboard
            this.dashboard?.load();
        } else if (this.isVisualizacaoModoList()) {
            this.list?.search();
            this.novoList?.search();
        }
    }

    /** Atualizando a pesquisa */
    refresh() {
        if (this.isVisualizacaoModoDashboard()) { // Dashboard
            this.dashboard?.load();
        } else if (this.isVisualizacaoModoList()) {
            this.list?.refresh();
        }
    }

    private closeFilter() {
        // this.clienteFilter.close();
        jQuery(this.containerContent.nativeElement).scrollTop(0);
    }


    insert() {
        this.baseVisualizacaoService.insert(this.activatedRoute);
    }


    /** Abre o modal para importar os clientes */
    openImportModal() {
        this.clienteImportModal.open();
    }

    /** Após importar os clientes */
    onAfterImport() {
        this.search();
    }

    /** Abre o modal para importar as unidades de negócio dos clientes */
    openImportUnidadeNegocioModal() {
        this.clienteUnidadeNegocioImportModal.open();
    }

    /** Após importar as unidades de negócio dos clientes */
    onAfterImportUnidadeNegocio() { }


    /** Abre o modal para realizar a impressão da listagem dos clientes */
    openListagemReportModal() {
        this.listagemReportModal.open(this.filter, this.orderBy, this.orderByDir);
    }

    /** Verifica se pode importar os clientes do ERP V1 */
    private canImportErp_v1(): boolean {
        // Somente se for a versão 1
        if (!this.isIntegracaoErpV1) {
            return false;
        }

        return this.authService.podeImportarErp();
    }

    /** Abre o modal para realizar a importação dos clientes do ERP V1 */
    openIntegracaoErpImportModal_v1() {
        this.integrationModal.open();
    }

    /** Após realizar a importação dos cliente do ERP V1 */
    afterImporErp_v1() {
        this.refresh();
    }





    /** Verifica se pode importar os clientes do ERP */
    private canImportErp(): boolean {
        // Somente se não for a versão 1
        if (this.isIntegracaoErpV1) {
            return false;
        }

        return this.authService.podeImportarErp();
    }

    /** Abre o modal para importar os clientes do ERP */
    openIntegracaoErpImportModal() {
        this.integracaoErpImportModal.open();
    }

    /** Após realizar a importação dos clientes do ERP */
    private afterImportErp() {
        this.afterImportErpSub$ = this.clienteIntegracaoErpImportService.afterImport
                                            .subscribe(
                                                () => this.refresh()
                                            );
    }

    openExcelExportContatoModal() {
        this.excelExportContatoModal?.open(this.filter);
    }

    /** Remove os storages das campanhas criados no form dos clientes */
    private removeCampanhasStorage() {
        this.campanhaVisualizacaoStorageService.remove(CAMPANHAS_FILTER_STORAGE_KEY);
        this.campanhaFilterStorageService.remove(CAMPANHAS_VISUALIZACAO_STORAGE_KEY);
    }

    ngAfterViewInit() {
        if (this.isVisualizacaoModoDashboard()) {
            setTimeout(() => this.search(), 0);
        }
    }

    ngOnDestroy(): void {
        this.afterImportErpSub$?.unsubscribe();
    }
}
